import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import * as Sentry from '@sentry/react'

import './index.css'
import App from './App.tsx'
import i18n from '@/lib/i18n'
import { initAnalytics } from '@/lib/analytics'
import { registerServiceWorker } from '@/lib/sw'

// Build-time env-var guard.
//
// Vite bakes `import.meta.env.VITE_*` values into the bundle at build
// time. If the build runs without `.env.local` present (e.g. inside a
// `.claude/worktrees/*` path that didn't inherit it from main), the
// values get hardcoded as `undefined`. `<ClerkProvider>` then throws
// "Missing publishableKey" at module load, React never mounts, and
// every user sees a blank page on every hostname.
//
// History: this happened in production on 2026-05-20 — deploy
// f4eed973 shipped with VITE_CLERK_PUBLISHABLE_KEY=undefined and
// blanked staging.autoflo.app + admin.autoflo.app until rollback.
//
// This guard short-circuits the React render and paints a clear,
// developer-actionable error page directly into #root so the next
// person hits a useful message instead of a blank screen.
const CLERK_PK = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
if (typeof CLERK_PK !== 'string' || CLERK_PK.length === 0) {
  renderEnvVarMissingError('VITE_CLERK_PUBLISHABLE_KEY')
  throw new Error(
    'VITE_CLERK_PUBLISHABLE_KEY missing — bundle was built without .env.local',
  )
}

function renderEnvVarMissingError(varName: string): void {
  const root = document.getElementById('root')
  if (!root) return
  const isProd = import.meta.env.PROD
  root.innerHTML = `
    <div style="
      min-height: 100dvh;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, system-ui, sans-serif;
      padding: 24px;
      background: #f8fafc;
    ">
      <div style="
        max-width: 560px;
        background: white;
        border: 1px solid #fee2e2;
        border-left: 4px solid #dc2626;
        border-radius: 8px;
        padding: 24px;
        color: #1f2937;
      ">
        <div style="font-weight: 700; color: #991b1b; font-size: 14px; letter-spacing: 0.05em; text-transform: uppercase;">
          Build configuration error
        </div>
        <h1 style="margin: 8px 0 12px; font-size: 20px; font-weight: 700;">
          autoflo can't start: missing ${varName}
        </h1>
        <p style="margin: 0 0 16px; font-size: 14px; line-height: 1.6; color: #4b5563;">
          This bundle was built without <code style="background: #f1f5f9; padding: 2px 6px; border-radius: 3px;">${varName}</code> set,
          so Clerk can't initialize. ${isProd ? 'This is a deploy bug — please report it.' : ''}
        </p>
        <div style="font-size: 13px; color: #4b5563; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px;">
          <strong>Most likely cause:</strong> the build ran in a directory
          that didn't have <code style="background: #f1f5f9; padding: 1px 4px; border-radius: 3px;">.env.local</code>.
          Copy it from the main repo and rebuild:
          <pre style="margin: 8px 0 0; padding: 8px; background: #1e293b; color: #e2e8f0; border-radius: 4px; overflow-x: auto; font-size: 12px;">cp /Users/agurin/code/autoflo/.env.local ./
npm run build</pre>
        </div>
      </div>
    </div>
  `
}

initAnalytics()

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event) {
      // PIPEDA: scrub VINs from URLs and breadcrumbs
      if (event.request?.url) {
        event.request.url = event.request.url.replace(
          /[A-HJ-NPR-Z0-9]{17}/g,
          '[VIN]',
        )
      }
      return event
    },
  })
}

const queryClient = new QueryClient()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* I18nextProvider sits above ClerkProvider so Clerk's surrounding
        chrome and any future auth-screen copy is also translatable. */}
    <I18nextProvider i18n={i18n}>
      <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}>
        <QueryClientProvider client={queryClient}>
          <Sentry.ErrorBoundary
            fallback={
              <div style={{ padding: 24, fontFamily: 'system-ui' }}>
                Something went wrong. We've been notified.
              </div>
            }
          >
            <App />
          </Sentry.ErrorBoundary>
        </QueryClientProvider>
      </ClerkProvider>
    </I18nextProvider>
  </StrictMode>,
)

// After render on purpose: the SW registers behind `load` (src/lib/sw.ts)
// so first paint never competes with it. Admin host: no-op inside.
registerServiceWorker()